export interface OilSpillLegendForecast {
  hour: number;
  color: string;
}

export interface OilSpillLegendGroup {
  id: string;
  label: string;
  color: string;
  forecasts: OilSpillLegendForecast[];
}

function normalizedClass(value: unknown): string | null {
  if (value == null || String(value).trim() === '') return null;
  return String(value).trim();
}

function forecastHour(value: unknown): number | null {
  if (value == null || String(value).trim() === '') return null;
  const hour = Number(value);
  return Number.isFinite(hour) && hour !== 0 ? hour : null;
}

function compareClasses(left: string, right: string): number {
  const numericDifference = Number(left) - Number(right);
  return Number.isNaN(numericDifference)
    ? left.localeCompare(right)
    : numericDifference;
}

function values(value: unknown): unknown[] {
  return Array.isArray(value) ? value : value == null ? [] : [value];
}

export function getOilSpillDetectionClasses(feedResults: any): string[] {
  if ((Array.isArray(feedResults?.detection_classes) && feedResults.detection_classes.length === 0) ||
      feedResults?.detection_class_count === 0) return [];

  const explicit = values(feedResults?.detection_classes)
    .map(normalizedClass).filter((value): value is string => value !== null);
  if (explicit.length) return [...new Set(explicit)].sort(compareClasses);

  // A count assumes the backend assigns sequential detection IDs 1..N.
  const count = Number(feedResults?.detection_count);
  if (feedResults?.detection_count != null && Number.isSafeInteger(count) && count >= 0 && count <= 1000) {
    return Array.from({ length: count }, (_, index) => String(index + 1));
  }
  return [];
}

export function getOilSpillForecastHours(feedResults: any): number[] {
  return [...new Set(values(
    feedResults?.forecast_hours ?? feedResults?.forecast_h ?? feedResults?.forecast_hour
  ).map(forecastHour).filter((value): value is number => value !== null))]
    .sort((left, right) => left - right);
}

/** Build the feed legend from information-service results, never viewport WFS features. */
export function buildOilSpillLegendGroupsFromResults(
  feedResults: any,
  config: any,
  fallbackColor: string,
  idPrefix: string,
  selection?: { detectionClass?: string | number; forecastHour?: string | number | null }
): OilSpillLegendGroup[] {
  if (!feedResults || typeof feedResults !== 'object') return [];

  const availableClasses = getOilSpillDetectionClasses(feedResults);
  const hasNoDetectionClasses = (Array.isArray(feedResults.detection_classes) &&
    feedResults.detection_classes.length === 0) || feedResults.detection_class_count === 0;
  const selectedClass = normalizedClass(selection?.detectionClass);
  const classes = selectedClass
    ? availableClasses.filter((value) => value === selectedClass) : availableClasses;
  const availableHours = getOilSpillForecastHours(feedResults);
  const selectedHour = forecastHour(selection?.forecastHour);
  const hours = selectedHour == null ? availableHours
    : availableHours.filter((hour) => hour === selectedHour);
  const groups = classes.map((detectionClass): OilSpillLegendGroup => ({
      id: `${idPrefix}:detection:${detectionClass}`,
      label: availableClasses.length === 1 ? 'Detection' : `Detection ${detectionClass}`,
      color: config?.detectionColorMap?.[detectionClass]?.color ?? fallbackColor,
      forecasts: hours.map((hour) => ({
        hour,
        color: config?.classForecastColorMap?.[detectionClass]?.[hour]?.color ??
          config?.forecastColorMap?.[hour]?.color ?? fallbackColor,
      })),
    }));

  if (!selectedClass && (feedResults.has_unclassified_detection_polygons ||
      feedResults.has_unclassified_forecast_points ||
      (classes.length === 0 && hasNoDetectionClasses) ||
      (classes.length === 0 && hours.length > 0 && feedResults.detection_count == null))) {
    groups.push({
      id: `${idPrefix}:detection:unclassified`,
      label: classes.length === 0 && !feedResults.has_unclassified_detection_polygons &&
        !hasNoDetectionClasses
        ? 'Forecast hours' : 'Detection',
      color: fallbackColor,
      forecasts: feedResults.has_unclassified_forecast_points || classes.length === 0
        ? hours.map((hour) => ({
          hour,
          color: config?.forecastColorMap?.[hour]?.color ?? fallbackColor,
        })) : [],
    });
  }

  return groups;
}
